/* @flow */

//----------------------------------------------------------------------//
//                         Imported NPM modules                         //
//----------------------------------------------------------------------//

import * as React from 'react';
import { inject } from 'mobx-react';
import { ResponsivePie } from '@nivo/pie';
import { Link } from 'react-router-dom';
import RequireForAccess from 'react-access';

//----------------------------------------------------------------------//
//                     Imported app modules/assets                      //
//----------------------------------------------------------------------//

import Button from '@material-ui/core/Button';
import Card from '@material-ui/core/Card';
import CardContent from '@material-ui/core/CardContent';
import CardHeader from '@material-ui/core/CardHeader';
import Divider from '@material-ui/core/Divider';

import DashboardStore from 'stores/DashboardStore';
import Loading from 'components/Loading/Loading';
import { allRoles } from 'permissions/general';

import type { CommonWidget } from '../../commonTypes';
import ChartLegend from '../ChartLegend/ChartLegend';
import ChartTable from '../ChartTable/ChartTable';
import InsufficientData from '../InsufficientData/InsufficientData';
import WidgetCard from '../WidgetCard';
import type { WidgetInfo } from '../WidgetCard';

import styles from './NivoPieChart.module.scss';

//----------------------------------------------------------------------//
//                       Exported React component                       //
//----------------------------------------------------------------------//

export type NivoPieChartProps = {
  tradingAccount?: string | number,
  data: Object,
  widgetInfos: WidgetInfo[],
  onVariantSelect?: (variant: string) => void,
  isLoading: boolean,
  totalKey: string,
  totalTitle?: (amount: number) => string,
  renderLegend?: () => React.Node,
  expandableLegend?: Object,
  button?: boolean,
  buttonLink?: string,
  buttonText?: string,
  permissions: ?Array<*>,
  isIE: boolean,
} & CommonWidget;

type State = {
  isLoading: boolean,
  lastPanel: number | null,
  pieData: any[],
  expandedPanel: number | null,
};

const initialState = (): State => ({
  isLoading: false,
  lastPanel: null,
  pieData: [],
  expandedPanel: null,
});

@inject('dashboardStore')
export default class NivoPieChart extends React.Component<NivoPieChartProps, State> {
  state = initialState();

  resetState() {
    this.setState(initialState());
  }

  componentDidUpdate(prevProps, prevState) {
    if (prevProps.tradingAccount !== this.props.tradingAccount) {
      this.resetState();
    }
  }

  async handleClick(id: string, i: number) {
    const { dashboardStore, tradingAccount } = this.props;

    if (this.state.expandedPanel === i) {
      this.setState({
        isLoading: false,
        lastPanel: i,
        expandedPanel: null,
      });
    } else {
      this.setState({
        isLoading: true,
        lastPanel: i,
        expandedPanel: i,
      });

      if (this.state.lastPanel !== i) {
        await dashboardStore[this.props.expandableLegend.loadData](tradingAccount, id);
      }
    }

    const { expandableLegend } = this.props;
    this.setState({
      isLoading: false,
      pieData:
        expandableLegend && expandableLegend.loadedData && expandableLegend.loadedData.length
          ? expandableLegend.loadedData
          : [],
    });
  }

  render() {
    const {
      widgetInfos,
      onVariantSelect,
      data,
      widgetItem,
      size,
      isLoading,
      totalKey,
      totalTitle,
      renderLegend,
      expandableLegend,
      button,
      buttonLink,
      buttonText,
      permissions,
      isIE,
      isFullScreen,
      ...other
    } = this.props;

    const isClickableClass = typeof other.onClick === 'function' ? styles['is-clickable'] : '';

    function firstToUpperCase(str) {
      return str.toUpperCase();
    }

    const isInteractive = size !== 'thumbnail';
    const margins = { top: 10, bottom: 10, left: 10, right: 10 };

    const totalAmount =
      data && data.length
        ? data.map(item => item[totalKey]).reduce((acc, cur) => acc + (cur || 0))
        : 0;

    return (
      <WidgetCard
        dataCount={totalAmount}
        isIE={isIE}
        loading={isLoading}
        permissions={permissions || allRoles}
        size={size}
        widgetInfos={widgetInfos}
        widgetItem={widgetItem}
        isFullScreen={isFullScreen}
        onVariantSelect={onVariantSelect}
        button={
          button && buttonLink && buttonText ? { link: buttonLink, label: buttonText } : undefined
        }
      >
        <React.Fragment>
          {size !== 'thumbnail' && <ChartLegend position="center" keys={data} />}
          {
            <div className={`${styles['full-container']} ${styles[size]}`}>
              <div className={`${styles['half-container']} ${styles['chart-wrapper']}`}>
                <div className={`${styles['pie-container']} ${isClickableClass}`}>
                  <ResponsivePie
                    data={data}
                    margin={margins}
                    innerRadius={0.5}
                    colorBy={(e: any) => e.color}
                    borderWidth={2}
                    borderColor="inherit"
                    enableRadialLabels={false}
                    enableSlicesLabels={false}
                    animate={false}
                    motionStiffness={90}
                    motionDamping={15}
                    isInteractive={isInteractive}
                    {...other}
                  />
                  {size !== 'thumbnail' && totalTitle && (
                    <div className={styles['total-count']}>
                      <strong>{totalAmount}</strong>
                      <span>{totalTitle(totalAmount)}</span>
                    </div>
                  )}
                </div>
              </div>
              {size === 'canvas' && (
                <div className={`${styles['half-container']} ${styles['legend-wrapper']}`}>
                  {data &&
                    (expandableLegend
                      ? data.map((item, index) => (
                          <ChartTable
                            key={item.id}
                            title={item.label}
                            number={item.value}
                            color={item.color}
                            onClick={e => this.handleClick(item.id, index)}
                            expanded={this.state.expandedPanel === index}
                          >
                            {this.state.isLoading ? (
                              <Loading />
                            ) : (
                              this.state.pieData.map(renderLegend)
                            )}
                          </ChartTable>
                        ))
                      : data.map(renderLegend))}
                </div>
              )}
            </div>
          }
        </React.Fragment>
      </WidgetCard>
    );
  }
}
